import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterModule } from '@angular/router';
import { BookingEducationApi } from '@core/apis/booking-education/booking-education.api';
import { BookingEducationProgramDTO } from '@core/apis/booking-education/booking-education.dto';
import { BookingRehabilitationApi } from '@core/apis/booking-rehabilitation/booking-rehabilitation.api';
import { BookingRehabilitationProgramDTO } from '@core/apis/booking-rehabilitation/booking-rehabilitation.dto';
import { BookingServiceApi } from '@core/apis/booking-service/booking-service.api';
import { BookingServiceDTO } from '@core/apis/booking-service/booking-service.dto';
import { EducationProgramDTO } from '@core/apis/education/education.dto';
import { RehabilitationProgramDTO } from '@core/apis/rehabilitation/rehabilitation.dto';
import { ServiceDTO } from '@core/apis/service/service.dto';
import { HeaderComponent } from '@core/components/header/header.component';
import { SidebarComponent } from '@core/components/sidebar/sidebar.component';
import { BookingStorageEnum } from '@core/enums/booking.enum';
import {
  IBookingBasketItem,
  IBookingChoosen,
  IBookingStorage,
  IBookingStorageDeleteEvent,
} from '@core/interfaces/booking.interface';
import { INavigation, IUserStore } from '@core/interfaces/interface';
import { ReusableOrderMethodsService } from '@core/services/reusable-order-methods.service';
import { StorageService } from '@core/services/storage.service';
import { UserStore } from '@core/stores/user.store';
import { TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin, map } from 'rxjs';
import { navigationPatient } from '../../navigation';
import { PatientNavigationEnum } from '../../navigation.enum';
import { GetIdsFromQueryPipe } from './get-ids-from-query.pipe';
import { EducationOrderComponent } from './order-items/education-order/education-order.component';
import { RehabilitationOrderComponent } from './order-items/rehabilitation-order/rehabilitation-order.component';
import { ServiceOrderComponent } from './order-items/service-order/service-order.component';
import { ServiceBookingStatusEnum, PaymentTypeEnum } from '@oranta-mis/sdk';

@Component({
  selector: 'app-basket',
  imports: [
    TranslocoModule,
    CommonModule,
    RouterModule,
    HeaderComponent,
    SidebarComponent,
    EducationOrderComponent,
    RehabilitationOrderComponent,
    ServiceOrderComponent,
  ],
  standalone: true,
  templateUrl: './basket.component.html',
  styleUrl: './basket.component.scss',
})
@UntilDestroy()
export class BasketComponent extends ReusableOrderMethodsService implements OnInit {
  public form!: FormGroup;
  public rehabilitationInit: RehabilitationProgramDTO[] = [];
  public educationInit: EducationProgramDTO[] = [];
  public serviceInit: ServiceDTO[] = [];
  public user: IUserStore | null = null;
  public navigation: INavigation[] = navigationPatient;
  public goToFastPay: boolean = false;
  private singleProgramBooking:
    | BookingRehabilitationProgramDTO
    | BookingEducationProgramDTO
    | BookingServiceDTO
    | undefined = undefined;

  constructor(
    protected override bookingRehabilitationApi: BookingRehabilitationApi,
    protected override bookingEducationApi: BookingEducationApi,
    protected override bookingServiceApi: BookingServiceApi,
    private readonly storageService: StorageService,
    private readonly userStore: UserStore,
    private route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
  ) {
    super(bookingEducationApi, bookingRehabilitationApi, bookingServiceApi);
  }

  ngOnInit(): void {
    this.getSelectedFromQuery();
    this.getSelected();
    this.user = this.userStore.get();
    this.reactiveForm();
  }

  public setProgramItem(item: IBookingBasketItem) {
    let index = this[item.type].controls.findIndex((control) => control.value.id === item.form.value.id);

    if (index !== -1) this[item.type].removeAt(index);
    this[item.type].push(item.form);
  }

  public checkout(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isLoading = true;

    const requests = [];

    const rehabilitationValue = this.form.get(BookingStorageEnum.rehabilitation)?.value;
    if (rehabilitationValue && rehabilitationValue.length > 0) {
      requests.push(
        this.bookingRehabilitationApi.create({
          programs: rehabilitationValue,
        }),
      );
    }

    const educationValue = this.form.get(BookingStorageEnum.education)?.value;
    if (educationValue && educationValue.length > 0) {
      requests.push(
        this.bookingEducationApi.create({
          educational: educationValue,
        }),
      );
    }

    const serviceValue = this.form.get(BookingStorageEnum.service)?.value;
    if (serviceValue && serviceValue.length > 0) {
      requests.push(
        this.bookingServiceApi.create({
          services: serviceValue,
        }),
      );
    }
    if (requests.length > 0) {
      forkJoin(requests)
        .pipe(
          untilDestroyed(this),
          map(
            (responses: (BookingServiceDTO[] | BookingRehabilitationProgramDTO[] | BookingEducationProgramDTO[])[]) => {
              const bookingPrograms: (
                | BookingRehabilitationProgramDTO
                | BookingEducationProgramDTO
                | BookingServiceDTO
              )[] = responses.reduce<
                (BookingRehabilitationProgramDTO | BookingEducationProgramDTO | BookingServiceDTO)[]
              >((acc, response) => {
                if (Array.isArray(response) && response.length > 0) {
                  return [...acc, ...response];
                }
                return acc;
              }, []);
              if (bookingPrograms.length === 1) {
                this.singleProgramBooking = bookingPrograms[0];
              } else {
                this.singleProgramBooking = undefined;
              }
            },
          ),
        )
        .subscribe({
          next: () => {
            this.isLoading = false;
            this.orderSuccess();
          },
          error: () => (this.isLoading = false),
        });
    } else {
      this.isLoading = false;
      console.log('No data to send');
      return;
    }
  }

  public onDelete(event: IBookingStorageDeleteEvent): void {
    this.storageService.deleteChosenProgram(event.type, event.program);
    const index = (this.form.get(event.type)?.value || []).controls.findIndex(
      (control: FormControl) => control.value.id === event.program.id,
    );
    if (index !== -1) (this[event.type] as any).removeAt(index);
  }

  private get [BookingStorageEnum.rehabilitation](): FormArray {
    this.form.get(BookingStorageEnum.rehabilitation)?.markAllAsTouched();
    return this.form.get(BookingStorageEnum.rehabilitation) as FormArray;
  }

  private get [BookingStorageEnum.education](): FormArray {
    this.form.get(BookingStorageEnum.education)?.markAllAsTouched();
    return this.form.get(BookingStorageEnum.education) as FormArray;
  }

  private get [BookingStorageEnum.service](): FormArray {
    this.form.get(BookingStorageEnum.service)?.markAllAsTouched();
    return this.form.get(BookingStorageEnum.service) as FormArray;
  }

  private orderSuccess() {
    const data: IBookingStorage = this.storageService.getChosenPrograms();
    let maxLength: number = 0;
    let maxKey: keyof typeof BookingStorageEnum = BookingStorageEnum.education;
    for (const key of Object.keys(data) as Array<keyof typeof BookingStorageEnum>) {
      const enumKey = key as keyof typeof BookingStorageEnum;
      const currentValue = data[enumKey];
      if (currentValue && Array.isArray(currentValue) && currentValue.length > maxLength) {
        maxLength = currentValue.length;
        maxKey = enumKey;
      }
    }
    localStorage.removeItem('chosenPrograms');
    this.storageService.chosenPrograms$.next({
      rehabilitation: null,
      education: null,
      service: null,
    });

    this.goToPaymentOrOrder(maxKey);
  }

  private goToPaymentOrOrder(maxKey: keyof typeof BookingStorageEnum): void {
    if (this.singleProgramBooking && this.singleProgramBooking.status === ServiceBookingStatusEnum.accepted) {
      this.pay(this.singleProgramBooking.id, BookingStorageEnum.education);
    } else {
      this.router.navigate([`/${PatientNavigationEnum.order}/${maxKey}`]);
    }
  }

  private getSelectedFromQuery(): void {
    const getIdsPipe = new GetIdsFromQueryPipe();
    this.route.queryParamMap
      .pipe(
        map((params: ParamMap) => ({
          rehabilitation: getIdsPipe.transform(params.get('r')),
          education: getIdsPipe.transform(params.get('e')),
          service: getIdsPipe.transform(params.get('s')),
        })),
        untilDestroyed(this),
      )
      .subscribe((booking) => {
        this.storageService.saveProgramsFromBooking(booking);
      });
  }

  private getSelected(): void {
    this.storageService.chosenPrograms$.pipe(untilDestroyed(this)).subscribe((choosen: IBookingChoosen) => {
      this.rehabilitationInit = choosen.rehabilitation || [];
      this.educationInit = choosen.education || [];
      this.serviceInit = choosen.service || [];
      // перевіряємо чи одна послуга вибрана та чи можливоа онлайн оплата та чи дозволено оплата без підтвердження
      const countItems =
        (choosen?.rehabilitation?.length ?? 0) + (choosen?.education?.length ?? 0) + (choosen?.service?.length ?? 0);
      if (countItems === 1) {
        const itemWithOnlinePayment = [
          ...(choosen.rehabilitation || []),
          ...(choosen.education || []),
          ...(choosen.service || []),
        ].find((item) => {
          // костиль через різні назви полів в dto, перехід на кемелкейс
          const paymentType = 'payment_type' in item ? item.payment_type : (item as ServiceDTO).paymentType;
          const paymentWithoutConfirm =
            'payment_without_confirm' in item
              ? item.payment_without_confirm
              : (item as ServiceDTO).paymentWithoutConfirm;
          return paymentType === PaymentTypeEnum.online && paymentWithoutConfirm === true;
        });
        this.goToFastPay = !!itemWithOnlinePayment;
      }
    });
  }

  private reactiveForm(): void {
    this.form = this.fb.group({
      [BookingStorageEnum.rehabilitation]: this.fb.array([]),
      [BookingStorageEnum.education]: this.fb.array([]),
      [BookingStorageEnum.service]: this.fb.array([]),
    });
  }
}
